import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { BlurImage } from '../BlurImage';
import { OfferBanner } from '../OfferBanner';
import { SaleOfferTag } from '../SaleOfferBanner';
import {
  ArrowRight, Building2, Check, MapPin, Users, Monitor, Coffee, Presentation, Armchair, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BOOKABLE_TYPES = [
  { id: 'all', label: 'All Types', icon: Building2 },
  { id: 'meeting_room', label: 'Meeting Rooms', icon: Monitor },
  { id: 'conference_room', label: 'Conference Rooms', icon: Presentation },
  { id: 'day_pass', label: 'Day Pass', icon: Coffee },
  { id: 'dedicated_desk', label: 'Dedicated Desk', icon: Armchair },
];

export const VenueSelector = ({
  regions, regionFilter, setRegionFilter,
  productTypes, productFilter, setProductFilter,
  filteredVenues, selectedVenue, setSelectedVenue,
  setSelectedBookable, bookablesRef,
  bookableTypes, bookableType, setBookableType,
  bookables, selectedBookable,
  onNext
}) => (
  <div className="space-y-8 animate-fade-in">
    <div>
      <h2 className="font-heading text-2xl font-bold mb-2">Select a Venue & Space</h2>
      <p className="text-muted-foreground">Choose your preferred location and workspace type</p>
    </div>

    {/* Filters */}
    <div className="flex flex-wrap gap-4 p-4 rounded-xl bg-secondary/30 border border-border">
      <div className="flex-1 min-w-[200px]">
        <Label className="text-sm text-muted-foreground mb-2 block">Filter by Region</Label>
        <div className="flex flex-wrap gap-2">
          {regions.map(region => (
            <button
              key={region.id}
              onClick={() => { setRegionFilter(region.id); setSelectedVenue(''); setSelectedBookable(''); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                regionFilter === region.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background border border-border hover:border-primary/50'
              }`}
            >
              {region.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-w-[200px]">
        <Label className="text-sm text-muted-foreground mb-2 block">Filter by Product</Label>
        <div className="flex flex-wrap gap-2">
          {productTypes.map(product => {
            const Icon = product.icon;
            return (
              <button
                key={product.id}
                onClick={() => setProductFilter(product.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  productFilter === product.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background border border-border hover:border-primary/50'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {product.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>

    {/* Venue Cards */}
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-lg font-heading">Choose Venue</Label>
        <span className="text-sm text-muted-foreground">{filteredVenues.length} venues found</span>
      </div>
      {filteredVenues.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No venues found for selected filters</p>
          <button onClick={() => { setRegionFilter('all'); setProductFilter('all'); }} className="text-primary hover:underline mt-2">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {filteredVenues.map(venue => {
            const imageUrl = venue.images?.[0]
              ? (venue.images[0].startsWith('/') ? `${BACKEND_URL}${venue.images[0]}` : venue.images[0])
              : 'https://images.unsplash.com/photo-1497366412874-3415097a27e7?w=400';
            const venueStatus = venue.venue_status || (venue.is_active ? 'active' : 'inactive');
            const isBookable = venueStatus === 'active';
            const statusConfig = {
              active: { label: 'Open for Booking', cls: 'bg-emerald-500/90 text-white' },
              inactive: { label: 'Bookings Unavailable', cls: 'bg-slate-500/90 text-white' },
              sold_out: { label: 'Sold Out', cls: 'bg-red-500/90 text-white' },
              upcoming: { label: 'Upcoming', cls: 'bg-blue-500/90 text-white' },
            }[venueStatus] || { label: venueStatus, cls: 'bg-slate-500/80 text-white' };

            return (
              <div
                key={venue.id}
                onClick={() => {
                  if (isBookable) {
                    setSelectedVenue(venue.id);
                    setSelectedBookable('');
                    setTimeout(() => bookablesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
                  }
                }}
                className={`rounded-xl overflow-hidden border-2 transition-all bg-card ${
                  !isBookable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  selectedVenue === venue.id
                    ? 'border-primary ring-2 ring-primary/20'
                    : isBookable ? 'border-border hover:border-primary/50 hover:shadow-lg' : 'border-border'
                }`}
                data-testid={`venue-card-${venue.id}`}
              >
                <div className="h-36 overflow-hidden relative">
                  <BlurImage src={imageUrl} thumbnail={venue.thumbnails?.[0]} alt={venue.name} className={`w-full h-full ${!isBookable ? 'grayscale' : ''}`} />
                  {selectedVenue === venue.id && (
                    <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow-lg z-10">
                      <Check className="w-4 h-4 text-primary-foreground" />
                    </div>
                  )}
                  <Badge className={`absolute top-3 left-3 text-xs z-10 ${statusConfig.cls}`}>{statusConfig.label}</Badge>
                  {venue.offer_enabled && (
                    <span
                      className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-white px-2.5 py-1 rounded-full shadow-md z-10"
                      style={{ background: 'linear-gradient(135deg, #CB6E4A 0%, #A6543F 100%)' }}
                      data-testid={`venue-offer-ribbon-${venue.id}`}
                    >
                      <Sparkles className="w-3 h-3" /> {venue.offer_badge || 'On Offer'}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h4 className="font-heading font-semibold text-base mb-1">{venue.name}</h4>
                  <p className="text-sm text-muted-foreground flex items-center">
                    <MapPin className="w-3.5 h-3.5 mr-1.5 text-primary" /> {venue.area_locality || venue.city}
                  </p>
                  {venue.nearest_metro && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center">
                      <span className="w-2 h-2 rounded-full bg-primary mr-2" />
                      {venue.nearest_metro} {venue.walk_time || venue.metro_distance}
                    </p>
                  )}
                  {venue.region && <p className="text-xs text-muted-foreground mt-1">{venue.region}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

    {/* Bookable Selection (shown when venue selected) */}
    {selectedVenue && (
      <div ref={bookablesRef}>
        <div className="space-y-4">
          <Label className="text-lg font-heading">Space Type</Label>
          <div className="flex flex-wrap gap-3">
            {bookableTypes.map(type => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => setBookableType(type.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                    bookableType === type.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-secondary/50 border-border hover:border-primary/50'
                  }`}
                  data-testid={`type-${type.id}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium text-sm">{type.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <Label className="text-lg font-heading">Available Spaces</Label>
          {bookables.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No spaces available for selected type</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bookables.map(bookable => {
                const isDisabled = !bookable.is_active;
                const isSelected = selectedBookable === bookable.id;
                const mode = bookable.booking_mode || 'hourly';
                const priceLabel = mode === 'monthly' ? '/month' : mode === 'daily' ? '/day' : '/hr';
                const inclusions = bookable.inclusions || bookable.whats_included || [];
                const exclusions = bookable.exclusions || bookable.whats_not_included || [];
                const capacityLabel = bookable.bookable_type === 'day_pass'
                  ? `${bookable.capacity} spots`
                  : `${bookable.capacity} ${bookable.capacity === 1 ? 'person' : 'people'}`;

                return (
                  <div
                    key={bookable.id}
                    onClick={() => !isDisabled && setSelectedBookable(bookable.id)}
                    className={`rounded-xl overflow-hidden border-2 transition-all ${
                      isDisabled ? 'border-border opacity-60 cursor-default'
                        : isSelected ? 'cursor-pointer border-primary ring-2 ring-primary/20'
                        : 'cursor-pointer border-border hover:border-primary/50 hover:shadow-md'
                    }`}
                    data-testid={`bookable-${bookable.id}`}
                  >
                    <div className="h-44 overflow-hidden relative">
                      <img
                        src={(() => {
                          const img = bookable.images?.[0];
                          if (!img) return 'https://images.unsplash.com/photo-1572025442646-866d16c84a54?w=600';
                          return img.startsWith('/') ? `${BACKEND_URL}${img}` : img;
                        })()}
                        alt={bookable.name}
                        className={`w-full h-full object-cover ${isDisabled ? 'grayscale' : ''}`}
                        loading="lazy"
                      />
                      {isDisabled && (
                        <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                          <span className="px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-500 text-sm font-semibold border border-amber-500/30">Coming Soon</span>
                        </div>
                      )}
                      {!isDisabled && isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                          <Check className="w-4 h-4 text-primary-foreground" />
                        </div>
                      )}
                      <Badge variant="secondary" className="absolute bottom-2 left-2 text-xs capitalize">
                        {bookable.bookable_type.replace('_', ' ')}
                      </Badge>
                      {bookable.offer_enabled && (
                        <Badge className="absolute top-2 left-2 border-0 text-white text-xs font-semibold shadow-sm" style={{ background: '#CB6E4A' }}>
                          {bookable.offer_badge || 'Offer'}
                        </Badge>
                      )}
                    </div>
                    <div className="p-4 bg-card space-y-3">
                      <OfferBanner venue={bookable} compact />
                      <SaleOfferTag bookableType={bookable.bookable_type} />

                      <div className="flex items-center justify-between">
                        <h4 className={`font-heading font-semibold text-sm ${isDisabled ? 'text-muted-foreground' : ''}`}>{bookable.name}</h4>
                        <span className={`text-sm font-bold ${isDisabled ? 'text-muted-foreground' : 'text-primary'}`}>
                          ₹{bookable.hourly_rate.toLocaleString()}{priceLabel}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Users className="w-3 h-3" /> {capacityLabel}
                        {bookable.gst_applicable && <span>+ GST</span>}
                      </div>
                      {isSelected && (inclusions.length > 0 || exclusions.length > 0) && (
                        <div className="pt-2 border-t border-border space-y-2">
                          {inclusions.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-green-600 mb-1">What's Included</p>
                              <div className="flex flex-wrap gap-1">
                                {inclusions.map((item, i) => (
                                  <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-950/30 text-green-600 border border-green-200 dark:border-green-800">
                                    ✓ {item}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {exclusions.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-red-500 mb-1">Not Included</p>
                              <div className="flex flex-wrap gap-1">
                                {exclusions.map((item, i) => (
                                  <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-950/30 text-red-500 border border-red-200 dark:border-red-800">
                                    ✗ {item}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    )}

    <div className="flex justify-end pt-4">
      <Button onClick={onNext} disabled={!selectedBookable} className="bg-primary hover:bg-primary/90" data-testid="next-step-1-btn">
        Continue <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
    </div>
  </div>
);
